class ProductPopup extends HTMLElement {
  constructor() {
    super();
    this.popupSection = this.closest('.section-product-popup');
    this.closeButton = this.querySelector('.product-close-popup');
    this.form = this.querySelector('#product-block-form');
    this.contentType = this.getAttribute('data-popup-trigger');
    this.maxShowTimes = parseInt(this.getAttribute('data-show-product'), 10) || 1;

    this.POPUP_DELAY = 6000;
    this.POPUP_DELAY_SCROLL = 10;

    this.closeCount = Number(localStorage.getItem('productPopupCloseCount')) || 0;
    this.formSubmitted = localStorage.getItem('productFormSubmitted') === 'true';
    this.popupShown = false;
    this.isDesignMode = window.Shopify && window.Shopify.designMode === true;

    this.init();
  }

  init() {
    if (!this.popupSection || this.closeCount >= this.maxShowTimes || this.formSubmitted) return;

    this.setupEventListeners();
    this.handleInitialScroll();
    setTimeout(() => this.handlePageLoadTimeout(), this.POPUP_DELAY);
  }

  setupEventListeners() {
    this.closeButton?.addEventListener('click', () => this.hidePopup());

    document.addEventListener('keydown', (event) => {
      const isEscape = event.key === 'Escape' || event.key === 'Esc';
      if (!isEscape) return;
      if (this.popupSection?.classList.contains('popup-visible')) {
        this.hidePopup();
      }
    });

    this.form?.addEventListener('submit', () => {
      localStorage.setItem('productFormSubmitted', 'true');
    });

    window.addEventListener('scroll', this.checkScroll.bind(this));
  }

  checkScroll() {
    if (this.getScrolledPercentage() > this.POPUP_DELAY_SCROLL) {
      window.removeEventListener('scroll', this.checkScroll);
      this.showPopup();
    }
  }

  handlePageLoadTimeout() {
    this.showPopup();
  }

  handleInitialScroll() {
    if (this.getScrolledPercentage() > this.POPUP_DELAY_SCROLL) {
      this.showPopup();
    }
  }

  getScrolledPercentage() {
    return ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100;
  }

  async showPopup() {
    if (this.popupShown) return;

    if (this.contentType === 'recently_product') {
      const raw = localStorage.getItem('recently-viewed-popup');
      if (!raw) return;

      let handle;
      try {
        const productData = JSON.parse(raw);
        handle = productData.productHandle;
      } catch (e) {
        console.error('Invalid JSON in recently-viewed-popup');
        return;
      }

      if (!handle) return;

      try {
        const fetchedData = await this.fetchProductData(handle);
        if (!fetchedData) return;

        this.updatePopupContent(fetchedData);
      } catch (e) {
        console.error('Failed to fetch product data:', e);
        return;
      }
    }

    this.popupSection.classList.remove('popup-hidden');
    this.popupSection.classList.add('popup-visible');
    if (!this.isDesignMode) this.removeAttribute('inert');
    this.popupShown = true;
  }

  hidePopup() {
    if (!this.popupSection) return;

    this.popupSection.classList.remove('popup-visible');
    this.popupSection.classList.add('popup-hidden');
    if (!this.isDesignMode) this.setAttribute('inert', '');
    localStorage.setItem('productPopupCloseCount', ++this.closeCount);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    document.querySelectorAll('localization-form').forEach((form) => {
      if (typeof form.hidePanel === 'function') form.hidePanel();
    });
  }

  addShopifyImageWidth(url, width = 600) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  formatMoney(cents, format = window.cartStrings?.moneyFormat) {
    if (cents == null || cents === '') return '';

    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return String(cents);

    const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';

      const value = (Number(number) / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? `${decimal}${parts[1]}` : '';

      return `${dollars}${decimals}`;
    };

    let amount = '';
    switch (match[1]) {
      case 'amount':
        amount = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        amount = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(cents, 2, "'");
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_period_and_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        amount = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, amount);
  }

  async fetchProductData(handle) {
    const url = `/products/${handle}.js`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const product = await response.json();
    const variant = product.variants.find(v => v.available) || product.variants[0];

    return {
      productImage: product.media?.[0]?.preview_image?.src ||
                   product.media?.[0]?.src ||
                   product.featured_image ||
                   product.images?.[0] || '',
      productTitle: product.title,
      productUrl: `/products/${product.handle}`,
      productPrice: this.formatMoney(variant.price),
      productPrice2: variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : ''
    };
  }

  updatePopupContent(productData) {
    if (!productData) return;

    const mediaBox = this.querySelector('.media-box img');
    if (mediaBox) {
      const optimizedImage = this.addShopifyImageWidth(productData.productImage, 690);
      mediaBox.setAttribute('src', optimizedImage);
      mediaBox.setAttribute('alt', productData.productTitle);
    }

    const title = this.querySelector('.product-block-title');
    if (title) title.textContent = productData.productTitle;

    const priceSale = this.querySelector('.price-item--sale');
    if (priceSale) priceSale.innerHTML = productData.productPrice;

    const priceRegular = this.querySelector('.price-item--regular');
    if (priceRegular) priceRegular.innerHTML = productData.productPrice2;

    const button = this.querySelector('.product-button a');
    if (button) button.setAttribute('href', productData.productUrl);
  }
}

customElements.define('product-popup', ProductPopup);
